import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import type { CardMachine } from './types';

export function useCardMachines() {
  const { clientId } = useCurrentClient();
  return useQuery({
    queryKey: ['card_machines', clientId],
    enabled: !!clientId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<CardMachine[]> => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('card_machines')
        .select('*')
        .eq('client_id', clientId)
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as CardMachine[];
    },
  });
}

export interface CardMachineInput {
  name: string;
  operator?: string;
  mdrDebitPct: number;
  mdrCreditPct: number;
  mdrInstallmentsPct: number;
  anticipationRateMonthlyPct: number;
  defaultAccountId?: string | null;
  notes?: string;
  isActive: boolean;
}

export function useUpsertCardMachine() {
  const qc = useQueryClient();
  const { clientId } = useCurrentClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: CardMachineInput }) => {
      if (!clientId) throw new Error('Cliente não selecionado');
      const payload = {
        client_id: clientId,
        name: input.name.trim(),
        operator: input.operator?.trim() || null,
        mdr_debit_pct: input.mdrDebitPct,
        mdr_credit_pct: input.mdrCreditPct,
        mdr_installments_pct: input.mdrInstallmentsPct,
        anticipation_rate_monthly_pct: input.anticipationRateMonthlyPct,
        default_account_id: input.defaultAccountId ?? null,
        notes: input.notes?.trim() || null,
        is_active: input.isActive,
      };
      if (id) {
        const { error } = await supabase.from('card_machines').update(payload).eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('card_machines').insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['card_machines'] });
      toast.success(vars.id ? 'Maquininha atualizada.' : 'Maquininha criada.');
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
}

export function useDeleteCardMachine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('card_machines')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['card_machines'] });
      toast.success('Maquininha excluída.');
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
}
